import { gql, useApolloClient, useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  CopyHeadAgreementInput,
  CopyHeadAgreementMutation,
  CopyHeadAgreementMutationVariables,
} from "@/gql/graphql";
import { errorFragment } from "@/gql/GqlErrorFragment";
import { customerDetailsQuery } from "@/features/product-and-price/customers/customer-details/hooks/useCustomerDetails";
import { P } from "@stenajs-webui/core";

const mutation = gql`
  ${errorFragment}
  mutation CopyHeadAgreement($input: CopyHeadAgreementInput!) {
    productPrice {
      headAgreement {
        copyHeadAgreement(input: $input) {
          ... on UpdateHeadAgreementSuccessResult {
            headAgreement {
              id
              rowVersion
              customer {
                id
              }
            }
          }
          ... on ErrorResult {
            ...ErrorFragment
          }
        }
      }
    }
  }
`;

export const useCopyHeadAgreementMutation = () => {
  const [mutate, { error, loading }] = useMutation<
    CopyHeadAgreementMutation,
    CopyHeadAgreementMutationVariables
  >(mutation, {
    update(cache, { data }) {
      const result = data?.productPrice.headAgreement.copyHeadAgreement;
      if (result?.__typename === "UpdateHeadAgreementSuccessResult") {
        const customerId = result?.headAgreement?.customer.id;
        if (customerId) {
          cache.evict({
            id: cache.identify({ __typename: "Customer", id: customerId }),
          });
          cache.gc();
        }
      }
    },
  });

  const copyHeadAgreement = useCallback(
    (input: CopyHeadAgreementInput) => mutate({ variables: { input } }),
    [mutate]
  );

  return {
    copyHeadAgreement,
    error,
    loading,
  };
};
